// Date/format helpers ported from the legacy useFileGroup store.

/**
 * Legacy semantics (server/utils/shareExpiration.ts): a share is expired when
 * its expiry date (normalized to UTC midnight) is <= today (UTC midnight), and
 * a missing expiry is treated as expired. Kept verbatim so status badges match
 * what legacy users saw.
 */
export function isShareExpired(expiresAt: string | Date | null | undefined): boolean {
  if (!expiresAt) return true;
  const expiresAtDate = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
  const currentDate = new Date();

  const expiryDayUTC = Date.UTC(
    expiresAtDate.getUTCFullYear(),
    expiresAtDate.getUTCMonth(),
    expiresAtDate.getUTCDate(),
  );

  const currentDayUTC = Date.UTC(
    currentDate.getUTCFullYear(),
    currentDate.getUTCMonth(),
    currentDate.getUTCDate(),
  );

  return expiryDayUTC <= currentDayUTC;
}

/**
 * Whole days between today (UTC midnight) and the expiry date (UTC midnight),
 * or null when no expiry is set. Negative values mean the date is past.
 */
export function daysRemaining(expiresAt: string | null | undefined): number | null {
  if (!expiresAt) return null;
  const expiryDate = new Date(expiresAt);
  const now = new Date();

  const expiryDayUTC = Date.UTC(
    expiryDate.getUTCFullYear(),
    expiryDate.getUTCMonth(),
    expiryDate.getUTCDate(),
  );

  const currentDayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

  return Math.round((expiryDayUTC - currentDayUTC) / 86400000);
}

export function formatTimeLeft(expiresAt: string | null | undefined): string {
  const days = daysRemaining(expiresAt);

  if (days === null) return "-";

  if (days < 0) return "Expiré";

  if (days === 0) return "Expire aujourd'hui";

  if (days === 1) return "Expire demain";

  return `${days} jour${days > 1 ? "s" : ""} restant${days > 1 ? "s" : ""}`;
}

// Canonical French formatters live in lib/shares/format.ts. Re-export so
// admin and outils converge on one plural/byte/date path (guideline §9.1, §8.2).
export { formatBytes, formatCount, formatNumber } from "@/lib/shares/format";

import {
  formatDate as formatDateFromDate,
  formatDateTime as formatDateTimeFromDate,
} from "@/lib/shares/format";

const EMPTY = "—";

export function formatDate(date: string | Date | null | undefined): string {
  if (date == null || date === "") return EMPTY;
  const d = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(d.getTime())) return EMPTY;

  return formatDateFromDate(d);
}

/** "4 juin 2026, 12 h 37" — America/Montreal via shared formatter. */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (date == null || date === "") return EMPTY;
  const d = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(d.getTime())) return EMPTY;

  return formatDateTimeFromDate(d);
}
